import {Link} from 'react-router-dom';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {BookingTrendData, UserGrowthData} from '../services/analyticsService';
import type {PlatformProvider} from '../services/providerManagement';
import type {SupportTicket} from '../services/supportTicketManagement';
import './DashboardHomeExtras.css';

const TREND_LABEL_DAYS = 14;

function formatAxisDate(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return iso;
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return new Date(iso).toLocaleDateString('en-IN', {month: 'short', day: 'numeric'});
}

function formatSubmitted(iso: string | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'});
}

function sortTicketsRecent(tickets: SupportTicket[], take: number): SupportTicket[] {
  return [...tickets]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, take);
}

function selectEscalations(tickets: SupportTicket[]): SupportTicket[] {
  return tickets
    .filter(
      (t) =>
        (t.priority === 'urgent' || t.priority === 'high') &&
        (t.status === 'open' || t.status === 'in_progress')
    )
    .sort((a, b) => {
      const rank = (p: SupportTicket['priority']) => (p === 'urgent' ? 0 : 1);
      const pr = rank(a.priority) - rank(b.priority);
      if (pr !== 0) return pr;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    })
    .slice(0, 12);
}

function tierLabel(priority: SupportTicket['priority']): 'P1' | 'P2' {
  return priority === 'urgent' ? 'P1' : 'P2';
}

export interface DashboardHomeExtrasProps {
  bookingTrends: BookingTrendData[];
  userGrowth: UserGrowthData[];
  ticketsRaw: SupportTicket[];
  pendingVerificationProviders: PlatformProvider[];
  chartsLoading: boolean;
  ticketsLoading: boolean;
  pendingVerificationLoading: boolean;
  chartsError: string | null;
  ticketsError: string | null;
  pendingVerificationError: string | null;
}

export function DashboardHomeExtras({
  bookingTrends,
  userGrowth,
  ticketsRaw,
  pendingVerificationProviders,
  chartsLoading,
  ticketsLoading,
  pendingVerificationLoading,
  chartsError,
  ticketsError,
  pendingVerificationError,
}: DashboardHomeExtrasProps) {
  const recentTickets = sortTicketsRecent(ticketsRaw, 8);
  const escalations = selectEscalations(ticketsRaw);

  return (
    <>
      <section className="dashboard-attention" aria-labelledby="dash-attention-heading">
        <h3 id="dash-attention-heading" className="dashboard-attention-title">
          Attention queue
        </h3>
        <div className="attention-cols">
          <div className={`attention-col attention-escalations ${escalations.length > 0 ? 'has-items' : ''}`}>
            <div className="attention-col-head">
              <h4>P1 / P2 tickets</h4>
              <span className="attention-hint">Urgent + High · open or in progress</span>
            </div>
            {ticketsError && <div className="panel-inline-error">{ticketsError}</div>}
            {ticketsLoading && <div className="panel-loading">Loading…</div>}
            {!ticketsLoading && !ticketsError && escalations.length === 0 && (
              <p className="panel-empty">No P1/P2 items in the current ticket batch.</p>
            )}
            {!ticketsLoading && escalations.length > 0 && (
              <ul className="escalation-list">
                {escalations.map((t) => (
                  <li key={t.ticketId}>
                    <Link
                      to={`/dashboard/support-tickets?ticketId=${encodeURIComponent(t.ticketId)}`}
                      className="escalation-link"
                    >
                      <span className={`esc-tier esc-tier-${tierLabel(t.priority)}`}>{tierLabel(t.priority)}</span>
                      <span className="esc-subject" title={t.subject}>
                        {t.subject.length > 48 ? `${t.subject.slice(0, 48)}…` : t.subject}
                      </span>
                      <span className="esc-meta">
                        <span className={`dash-badge dash-status dash-status-${t.status}`}>
                          {t.status.replace(/_/g, ' ')}
                        </span>
                        <span className="ticket-mini-time">{formatRelative(t.updatedAt)}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/dashboard/support-tickets" className="attention-footer-link">
              Full support queue →
            </Link>
          </div>

          <div
            className={`attention-col attention-verification ${
              pendingVerificationProviders.length > 0 ? 'has-items' : ''
            }`}
          >
            <div className="attention-col-head">
              <h4>Provider verification</h4>
              <span className="attention-hint">Pending review (FIFO by submitted)</span>
            </div>
            {pendingVerificationError && <div className="panel-inline-error">{pendingVerificationError}</div>}
            {pendingVerificationLoading && <div className="panel-loading">Loading…</div>}
            {!pendingVerificationLoading && !pendingVerificationError && pendingVerificationProviders.length === 0 && (
              <p className="panel-empty">No providers waiting in pending review (in scanned set).</p>
            )}
            {!pendingVerificationLoading && pendingVerificationProviders.length > 0 && (
              <ul className="verification-queue-list">
                {pendingVerificationProviders.map((p) => (
                  <li key={p.providerId}>
                    <Link to={`/dashboard/providers/${p.providerId}`} className="verification-queue-link">
                      <span className="vq-email" title={p.email ?? p.providerId}>
                        {(p.email ?? p.providerId).length > 36
                          ? `${(p.email ?? p.providerId).slice(0, 36)}…`
                          : p.email ?? p.providerId}
                      </span>
                      <span className="vq-meta">
                        Submitted {formatSubmitted(p.verification?.submittedAt)}
                        {p.location?.city && ` · ${p.location.city}`}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/dashboard/providers" className="attention-footer-link">
              All providers →
            </Link>
          </div>
        </div>
      </section>

      <div className="dashboard-extras-grid">
          <section className="dashboard-panel dashboard-panel-charts" aria-labelledby="dash-charts-heading">
            <h3 id="dash-charts-heading">Trends (last {TREND_LABEL_DAYS} days)</h3>
            <p className="panel-lead">
              Snapshot only — explore <Link to="/dashboard/analytics">Analytics</Link> for filters and exports.
            </p>
            {chartsError && bookingTrends.length === 0 && userGrowth.length === 0 && (
              <div className="panel-inline-error">{chartsError}</div>
            )}
            {chartsError && (bookingTrends.length > 0 || userGrowth.length > 0) && (
              <p className="panel-lead panel-warn">{chartsError}</p>
            )}
            {chartsLoading && <div className="panel-loading">Loading charts…</div>}
            {!chartsLoading && bookingTrends.length === 0 && userGrowth.length === 0 && !chartsError && (
              <p className="panel-empty">No trend data yet. Check back after bookings and signups accrue.</p>
            )}
            <div className="charts-row">
              {bookingTrends.length > 0 && (
                <div className="mini-chart-card">
                  <h4>Bookings</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={bookingTrends} margin={{top: 8, right: 12, left: 0, bottom: 0}}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" tick={{fontSize: 11}} tickFormatter={formatAxisDate} />
                      <YAxis width={32} tick={{fontSize: 11}} allowDecimals={false} />
                      <Tooltip />
                      <Legend wrapperStyle={{fontSize: 12}} />
                      <Line type="monotone" dataKey="bookings" name="Created" stroke="#6366f1" strokeWidth={2} dot={false} />
                      <Line
                        type="monotone"
                        dataKey="completed"
                        name="Completed"
                        stroke="#10b981"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
              {userGrowth.length > 0 && (
                <div className="mini-chart-card">
                  <h4>New users / day</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={userGrowth} margin={{top: 8, right: 12, left: 0, bottom: 0}}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" tick={{fontSize: 11}} tickFormatter={formatAxisDate} />
                      <YAxis width={32} tick={{fontSize: 11}} allowDecimals={false} />
                      <Tooltip />
                      <Legend wrapperStyle={{fontSize: 12}} />
                      <Line type="monotone" dataKey="count" name="New users" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </section>

          <div className="dashboard-side-stack">
            <section className="dashboard-panel" aria-labelledby="dash-tickets-heading">
              <div className="panel-heading-row">
                <h3 id="dash-tickets-heading">Latest support tickets</h3>
                <Link to="/dashboard/support-tickets" className="panel-link-all">
                  Queue
                </Link>
              </div>
              {ticketsError && <div className="panel-inline-error">{ticketsError}</div>}
              {ticketsLoading && <div className="panel-loading">Loading tickets…</div>}
              {!ticketsLoading && !ticketsError && recentTickets.length === 0 && (
                <p className="panel-empty">No tickets in the recent fetch.</p>
              )}
              {!ticketsLoading && recentTickets.length > 0 && (
                <ul className="ticket-mini-list">
                  {recentTickets.map((t) => (
                    <li key={t.ticketId}>
                      <Link
                        to={`/dashboard/support-tickets?ticketId=${encodeURIComponent(t.ticketId)}`}
                        className="ticket-mini-link"
                      >
                        <span className="ticket-mini-subject" title={t.subject}>
                          {t.subject.length > 52 ? `${t.subject.slice(0, 52)}…` : t.subject}
                        </span>
                        <span className="ticket-mini-meta">
                          <span className={`dash-badge dash-status dash-status-${t.status}`}>
                            {t.status.replace(/_/g, ' ')}
                          </span>
                          <span className={`dash-badge dash-priority dash-priority-${t.priority}`}>{t.priority}</span>
                          <span className="ticket-mini-time">{formatRelative(t.updatedAt)}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="dashboard-panel dashboard-panel-tips" aria-labelledby="dash-tips-heading">
              <h3 id="dash-tips-heading">Useful references</h3>
              <ul className="tips-list">
                <li>
                  <strong>P1 / P2</strong> — platform mapping: <strong>P1</strong> = urgent, <strong>P2</strong> = high
                  priority; attention queue lists open and in-progress cases only.
                </li>
                <li>
                  <strong>Verification</strong> — providers in <strong>pending review</strong> need admin action on the
                  provider detail (Verification tab) before regulated categories go live.
                </li>
                <li>
                  <strong>Cancellations</strong> — captured payments refund automatically when a booking is cancelled or
                  rejected (Razorpay). Failed refunds surface as API errors for manual follow-up.
                </li>
                <li>
                  <strong>Revenue and commission</strong> — standard %, reduced %, and fixed fees per work category in{' '}
                  <Link to="/dashboard/system-settings">System settings</Link> (payout policy panel); per-provider models
                  on each <Link to="/dashboard/providers">provider</Link> detail.
                </li>
                <li>
                  <Link to="/dashboard/analytics">Analytics</Link> · <Link to="/dashboard/reports">Reports</Link> ·{' '}
                  <Link to="/dashboard/customer-ads">Customer ads</Link> · <Link to="/dashboard/promotions">Promotions</Link>
                </li>
              </ul>
            </section>
          </div>
        </div>
    </>
  );
}
